import { ONE_DAY_IN_MS } from "features/CenterContent/RoleContent/VehicleMap/constants";
import {
  clearAllToasts,
  useOnlineStatus,
} from "features/CenterContent/shared/utils";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { createToast } from "shared/components/Spor";

interface NetworkToastContextValue {
  sendNetworkToast: () => void;
}

const initialValue: NetworkToastContextValue = {
  sendNetworkToast: () => {},
};

const NetworkToastContext = React.createContext(initialValue);

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef<() => void>(undefined);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }
    // Null stops interval
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return undefined;
  }, [delay]);
}

/**
 * Sends a toast if there are no network connection
 */
const NetworkToastProvider = ({ children }: { children: ReactNode }) => {
  const [showingToast, setShowingToast] = useState(false);
  const timerRef = useRef<number | null>(null);
  const isOnline = useOnlineStatus();
  const { t } = useTranslation();

  // Sends a Network Toast that lasts for 10 seconds. If one is already showing, reset timer to 10 seconds.
  const sendNetworkToast = useCallback(() => {
    setShowingToast(() => {
      if (!showingToast) {
        createToast({
          variant: "critical",
          text: t(
            "Det er problemer med tilkoblingen til serveren. Vennligst kontroller din nettverksforbindelse, eller last inn siden på nytt.",
          ),
          duration: ONE_DAY_IN_MS,
        });
        return true;
      }
      return showingToast;
    });
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setShowingToast(false);
      clearAllToasts();
    }, 10000);
  }, []);

  // Send a Network Toast every 5 second if !IsOnline
  useInterval(sendNetworkToast, isOnline ? null : 5000);

  const value = useMemo(() => ({ sendNetworkToast }), [sendNetworkToast]);

  return (
    <NetworkToastContext.Provider value={value}>
      {children}
    </NetworkToastContext.Provider>
  );
};

export { NetworkToastProvider };
